import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { TableLayout, type TableColumn } from "../../../components/dashboard/TableLayout";
import { api } from "../../../lib/api";
import { formatInrWhole } from "../../../lib/currency";
import { useAuth } from "../../../providers/AuthProvider";
import type { AdminDashboardResponse } from "../../../lib/api";

type SaleRow = {
  invoice: string;
  business: string;
  amount: string;
  time: string;
};

type ProcurementRow = {
  po: string;
  business: string;
  supplier: string;
  time: string;
};

type BusinessRow = {
  name: string;
  owner: string;
  status: string;
  created: string;
};

const salesColumns: TableColumn<SaleRow>[] = [
  { key: "invoice", header: "Invoice", render: (r) => <span className="font-medium">{r.invoice}</span> },
  { key: "business", header: "Business", render: (r) => <span className="text-muted-foreground">{r.business}</span> },
  { key: "amount", header: "Amount", render: (r) => <span>{r.amount}</span> },
  { key: "time", header: "Time", render: (r) => <span className="text-muted-foreground">{r.time}</span> },
];

const procurementColumns: TableColumn<ProcurementRow>[] = [
  { key: "po", header: "PO", render: (r) => <span className="font-medium">{r.po}</span> },
  { key: "business", header: "Business", render: (r) => <span className="text-muted-foreground">{r.business}</span> },
  { key: "supplier", header: "Supplier", render: (r) => <span>{r.supplier}</span> },
  { key: "time", header: "Time", render: (r) => <span className="text-muted-foreground">{r.time}</span> },
];

const businessColumns: TableColumn<BusinessRow>[] = [
  { key: "name", header: "Business", render: (r) => <span className="font-medium">{r.name}</span> },
  { key: "owner", header: "Owner", render: (r) => <span className="text-muted-foreground">{r.owner}</span> },
  { key: "status", header: "Status", render: (r) => <span className="capitalize">{r.status}</span> },
  { key: "created", header: "Created", render: (r) => <span className="text-muted-foreground">{r.created}</span> },
];

type RecentActivityRow = AdminDashboardResponse["data"]["recent_activity"][number];

export function AdminLatestTables({
  dashboardRecentActivity,
  businessRows,
}: {
  dashboardRecentActivity?: RecentActivityRow[];
  businessRows?: BusinessRow[];
}) {
  const { token } = useAuth();
  const [latestSales, setLatestSales] = useState<SaleRow[]>([]);
  const [latestProcurements, setLatestProcurements] = useState<ProcurementRow[]>([]);
  const [latestBusinesses, setLatestBusinesses] = useState<BusinessRow[]>([]);

  const formatMoney = useMemo(
    () => (v: string | number | null | undefined) => {
      const n = typeof v === "number" ? v : Number(v ?? 0);
      return formatInrWhole(n);
    },
    [],
  );

  const relativeTime = useMemo(() => {
    const fmt = (msAgo: number) => {
      const s = Math.floor(msAgo / 1000);
      const m = Math.floor(s / 60);
      const h = Math.floor(m / 60);
      const d = Math.floor(h / 24);
      if (s < 60) return `${s}s ago`;
      if (m < 60) return `${m}m ago`;
      if (h < 24) return `${h}h ago`;
      return `${d}d ago`;
    };

    return (iso: string | null | undefined) => {
      if (!iso) return "—";
      const t = new Date(iso).getTime();
      if (!Number.isFinite(t)) return "—";
      return fmt(Date.now() - t);
    };
  }, []);

  useEffect(() => {
    if (dashboardRecentActivity !== undefined && businessRows !== undefined) {
      const sales = dashboardRecentActivity
        .filter((r) => r.type === "sale")
        .slice(0, 5)
        .map((r) => ({
          invoice: r.reference,
          business: r.label,
          amount: formatMoney(r.amount),
          time: relativeTime(r.created_at),
        }));
      const pos = dashboardRecentActivity
        .filter((r) => r.type === "purchase")
        .slice(0, 5)
        .map((r) => ({
          po: r.reference,
          business: r.label,
          supplier: "—",
          time: relativeTime(r.created_at),
        }));
      setLatestSales(sales);
      setLatestProcurements(pos);
      setLatestBusinesses(businessRows);
      return;
    }
  }, [dashboardRecentActivity, businessRows, formatMoney, relativeTime]);

  useEffect(() => {
    if (dashboardRecentActivity !== undefined && businessRows !== undefined) return;
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const [ordersRes, businessesRes] = await Promise.all([
          api.adminReports.getRecentOrders({ token, limit: 10 }),
          api.admin.getBusinesses({ token }),
        ]);

        if (cancelled) return;

        const orders = ((ordersRes.data as any[]) ?? []) as Array<{
          order_type: string;
          order_id: string;
          business_name: string;
          customer: string;
          amount: string;
          created_at: string | null;
        }>;

        setLatestSales(
          orders
            .filter((r) => r.order_type === "Sale")
            .slice(0, 5)
            .map((r) => ({
              invoice: r.order_id,
              business: r.business_name,
              amount: formatMoney(r.amount),
              time: relativeTime(r.created_at),
            })),
        );

        setLatestProcurements(
          orders
            .filter((r) => r.order_type === "PO")
            .slice(0, 5)
            .map((r) => ({
              po: r.order_id,
              business: r.business_name,
              supplier: r.customer,
              time: relativeTime(r.created_at),
            })),
        );

        setLatestBusinesses(
          ((businessesRes as any[]) ?? []).slice(0, 5).map((b) => ({
            name: String(b.name ?? ""),
            owner: String(b.ownerName ?? b.ownerEmail ?? "—"),
            status: String(b.status ?? "pending"),
            created: relativeTime(b.verifiedAt ?? null),
          })),
        );
      } catch {
        // ignore - keep empty
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, formatMoney, relativeTime, dashboardRecentActivity, businessRows]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle>Latest sales</CardTitle>
        </CardHeader>
        <CardContent>
          <TableLayout columns={salesColumns} rows={latestSales} />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle>Latest procurements</CardTitle>
        </CardHeader>
        <CardContent>
          <TableLayout columns={procurementColumns} rows={latestProcurements} />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle>Latest businesses</CardTitle>
        </CardHeader>
        <CardContent>
          <TableLayout columns={businessColumns} rows={latestBusinesses} />
        </CardContent>
      </Card>
    </div>
  );
}
